import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import './Settings.css';
import api from '../../api/axios';
import { FiLock, FiLogOut, FiUser } from 'react-icons/fi';
import { logoutAdmin } from '../../utils/auth';
import { useOtpTimer } from '../../hooks/useOtpTimer';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [email, setEmail] = useState(''); // email instead of phoneNumber
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetToken, setResetToken] = useState(''); // store token after OTP verification

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  const [newPasswordMismatch,setNewpasswordMismatch]=useState(false)
  const [otpSent, setOtpSent] = useState(false);
  const otpTimer = useOtpTimer();

  const navigate = useNavigate();

  // Fetch profile on profile tab
  useEffect(() => {
    if (activeTab !== "profile") return;

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await api.get("/user/profile");
        if (res.data?.success) {
          setProfile(res.data.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [activeTab]);

  // ================== FORGOT PASSWORD FLOW ==================

  const handleResetPassword = () => {
    setShowOtpVerification(true);
  };

  const handleSendOtp = async (isResend = false) => {
    if (!email) return alert("Please enter your email");
    try {
      setIsLoading(true);
      await api.post("/admin/forgot-password/send-otp", { email });
      setOtpSent(true);
      otpTimer.startTimer();
      toast.success(isResend ? "OTP resent to your email" : "OTP sent to your email");
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setIsLoading(false)
    }
  };

  const handleResendOtp = async () => {
    if (!otpTimer.canResend || isLoading) return;
    setOtp(['', '', '', '', '', '']);
    await handleSendOtp(true);
  };

const handleOtpChange = (index, value) => {
  const newOtp = [...otp];

  if (value.length > 1) return; // ignore more than 1 char

  newOtp[index] = value;
  setOtp(newOtp);

  if (value) {
    // Move to next input if value entered
    if (index < 5) document.getElementById(`otp-${index + 1}`)?.focus();
  } else {
    // If empty (backspace), move focus to previous input
    if (index > 0) document.getElementById(`otp-${index - 1}`)?.focus();
  }
};


  const handleVerifyAccount = async () => {
    const enteredOtp = otp.join('');
    if (enteredOtp.length !== 6) return alert("Enter complete OTP");

    try {
      setIsLoading(true);
      const res = await api.post("/admin/forgot-password/verify-otp", {
        email,
        otp: enteredOtp
      });
      setResetToken(res.data.resetToken);
      setShowNewPassword(true);
      otpTimer.resetTimer();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "OTP verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitNewPassword = async () => {
    if (!newPassword || !confirmPassword) return alert("Enter all fields");
    // if (newPassword !== confirmPassword) return alert("Passwords do not match");
    if(newPassword!==confirmPassword){
      setNewpasswordMismatch(true)
      return
    }
    if (!resetToken) return alert("Reset token missing. Please verify OTP again.");

    try {
      setIsLoading(true);
      await api.post("/admin/forgot-password/reset", {
        resetToken,
        newPassword,
        confirmPassword
      });
      toast.success("Password reset successfully");
      // Reset states
      setShowOtpVerification(false);
      setShowNewPassword(false);
      setActiveTab('profile');
      setEmail('');
      setOtp(['', '', '', '', '', '']);
      setNewPassword('');
      setConfirmPassword('');
      setResetToken('');
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Password reset failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Back arrow: New Password → OTP screen
  const handleBackToOtp = () => {
    setShowNewPassword(false);
    setShowOtpVerification(true);
    setEmail('');                     
  setOtp(['', '', '', '', '', '']); 
  setResetToken('');
  setOtpSent(false);
  otpTimer.resetTimer();
  };

  const handleBackToReset = () => {
    setShowOtpVerification(false);
    setShowNewPassword(false);
    setEmail('');
  setOtp(['', '', '', '', '', '']);
  setResetToken('');
  setOtpSent(false);
  otpTimer.resetTimer();
  };
//   const handleLogout = async () => {
//   try {
//     await api.post("/user/logout");
//     toast.success('logged out successfully')
//     // Clear token from local storage or cookie
//     localStorage.removeItem("token"); // or your getToken() storage
//     window.location.href = "/"; // redirect to login
//   } catch (err) {
//     console.error(err);
//     alert("Logout failed");
//   }
// };

const handleLogout = async () => {
  try {
    await api.post("/user/logout"); // optional
    logoutAdmin(); // clear token and admin data
    toast.success("Logged out successfully");
    navigate("/", { replace: true }); // replace prevents back navigation to dashboard
  } catch (err) {
    console.error(err);
    toast.error("Logout failed");
  }
};

  // ================== RENDER ==================
  return (
    <div className="settings">
      <div className="settings-content">
        <div className="settings-sidebar">
          <div className="sidebar-header">
            <h3>Settings (Profile)</h3>
          </div>
          <div className="sidebar-menu">
            <button 
              className={`menu-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <span className="menu-icon"><FiUser /></span>
              Profile
            </button>
            <button 
              className={`menu-item ${activeTab === 'forgot-password' ? 'active' : ''}`}
              onClick={() => setActiveTab('forgot-password')}
            >
              <span className="menu-icon"><FiLock /></span>
              Forgot Password
            </button>
            <button className="menu-item logout" onClick={handleLogout}>
              <span className="menu-icon"><FiLogOut /></span>
              Logout
            </button>
          </div>
        </div>

        <div className="settings-main">
          {activeTab === 'profile' && (
            <>
              <div className="profile-header">
                <h2>Our Profile</h2>
              </div>
              <div className="profile-content">
                <h3>Personal Information</h3>
                {loading && <p>Loading profile...</p>}
                {profile && (
                  <div className="info-grid2">
                    <div className="info-item">
                      <label>Name</label>
                      <span className="info-value highlight">{profile?.name || "name"}</span>
                    </div>
                    <div className="info-item">
                      <label>Status</label>
                      <span className="info-value">{profile.status || "status"}</span>
                    </div>
                    <div className="info-item">
                      <label>Email</label>
                      <span className="info-value">{profile.email || "email"}</span>
                    </div>
                    <div className="info-item">
                      <label>Phone No.</label>
                      <span className="info-value">{profile.mobile_number || "mobile"}</span>
                    </div>
                    <div className="info-item">
                      <label>Profile Completion</label>
                      <span className="info-value">{profile.profileCompletion || "profileCompletion"}</span>
                    </div>
                    <div className="info-item">
                      <label>AADHAR NO</label>
                      <span className="info-value">{profile.adhaar_card_number || "Not added"}</span>
                    </div>
                  </div> 
                )}
              </div>
            </>
          )}

          {activeTab === 'forgot-password' && (
            <>
              <div className="profile-header">
                <h2>Forgot Password</h2>
              </div>

              {!showOtpVerification && !showNewPassword ? (
                <div className="forgot-password-content">
                  <div className="forgot-password-logo">
                    <img src="/bandhanguru_logo.svg" alt="Bandhan Guru" className="logo" />
                  </div>
                  <div className="forgot-password-text">
                    <h3>No worries, it happens to the best of us.</h3>
                    <p>Enter your registered email address, and we'll send you a secure link to reset your password.</p>
                  </div>
                  <button onClick={handleResetPassword} className="reset-btn">
                    Reset Password
                  </button>
                </div>
              ) : showOtpVerification && !showNewPassword ? (
                <div className="otp-verification-content">
                  <div className="forgot-password-logo">
                    <img src="/bandhanguru_logo.svg" alt="Bandhan Guru" className="logo" />
                  </div>
                  <div className="otp-text">
                    <h3>Verify your account by entering the OTP sent to you.</h3>
                  </div>
                  <div className="phone-section">
                    <label>Email Address</label>
                    <div className="phone-input-group">
                      <input
                        type="email"
                        placeholder="Enter email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="phone-input"
                      />
                      <button 
                        onClick={() => handleSendOtp(false)}
                        className="send-otp-btn"
                        disabled={isLoading || otpSent}
                      >
                        {isLoading ? 'Sending...' : 'Send OTP'}
                      </button>
                    </div>
                  </div>
                  <div className="otp-section">
                    <label>OTP</label>
                    <div className="otp-inputs">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          maxLength="1"
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Backspace" && !otp[index] && index > 0) {
                              document.getElementById(`otp-${index - 1}`)?.focus();
                            }
                          }}
                          className="otp-input"
                        />
                      ))}
                    </div>
                    {otpSent && (
                      <div className="otp-resend-row">
                        {otpTimer.canResend ? (
                          <>
                            <span className="otp-timer-text">Didn't receive the OTP?</span>
                            <button
                              type="button"
                              className="otp-resend-btn"
                              onClick={handleResendOtp}
                              disabled={isLoading}
                            >
                              {isLoading ? 'Sending...' : 'Resend OTP'}
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="otp-timer-text">
                              OTP expires in {otpTimer.formattedTime}
                            </span>
                            <span className="otp-timer-text">
                              Resend OTP in {otpTimer.formattedTime}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <button onClick={handleVerifyAccount} className="verify-btn">
                    Verify your Account
                  </button>
                   <button onClick={handleBackToReset} className="back-reset-btn">
                     &larr; Back to Reset
                  </button>
                </div>
              ) : (
                <div className="new-password-content">
                  <div className="forgot-password-logo">
                    <img src="/logo.png" alt="Bandhan Guru" className="logo" />
                  </div>
                  <div className="new-password-text">
                    <h3>Please create a new password to regain access to your BandhanGuru account.</h3>
                  </div>
                  <div className="password-form">
                    <div className="input-group">
                      <label>New Password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="password-input"
                      />
                    </div>
                    <div className="input-group">
                      <label>Confirm Password</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="password-input"
                      />
                    </div>
                    {newPasswordMismatch && (
                    <p className='error-warning'>password and confirm password does not match</p>
                    )}
                    <button 
                      onClick={handleSubmitNewPassword}
                      className="submit-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Submitting...' : 'Submit'}
                    </button>
                     <button onClick={handleBackToOtp} className="back-verify-btn">
                     &larr; Back to Verify Account
                  </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
       <ToastContainer
        toastClassName="custom-toast"
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default Settings;
